import React, { useCallback } from 'react';

import { alpha } from '@mui/material/styles';
import type { LucideIcon } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

export interface SegmentedControlOption {
  readonly label: string;
  readonly value: string;
  /** Drawn before the label. */
  readonly icon?: LucideIcon;
}

interface SegmentedControlProps {
  /** Names the group, so a screen reader hears what the options switch between. */
  readonly ariaLabel: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly SegmentedControlOption[];
  readonly value: string;
  readonly className?: string;
}

const useStyles = makeStyles()(theme => ({
  option: {
    '&:hover': {
      backgroundColor: alpha(theme.appColors.primary, 0.04),
    },
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.appColors.rule}`,
    },
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.appColors.secondaryType,
    cursor: 'pointer',
    display: 'inline-flex',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
    gap: theme.spacing(0.5),
    letterSpacing: 'normal',
    lineHeight: 'normal',
    padding: theme.spacing(0.25, 1),
    textAlign: 'center',
    transition: 'background-color 120ms ease, color 120ms ease',
  },
  optionSelected: {
    '&:hover': {
      backgroundColor: alpha(theme.appColors.primary, 0.12),
    },
    backgroundColor: alpha(theme.appColors.primary, 0.08),
    color: theme.appColors.primary,
    fontWeight: 600,
  },
  root: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 6,
    display: 'inline-flex',
    flexShrink: 0,
    overflow: 'hidden',
  },
}));

const SegmentedControlButton = React.memo(function SegmentedControlButton({
  onChange,
  option,
  selected,
}: {
  readonly onChange: (value: string) => void;
  readonly option: SegmentedControlOption;
  readonly selected: boolean;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const handleClick = useCallback(() => onChange(option.value), [onChange, option.value]);
  const Icon = option.icon;
  return (
    <button
      aria-pressed={selected}
      className={cx(classes.option, selected && classes.optionSelected)}
      type="button"
      onClick={handleClick}
    >
      {Icon && <Icon aria-hidden size={14} />}
      {option.label}
    </button>
  );
});

/**
 * One-of-N switch for a view, sized to sit inline next to other controls.
 *
 * Values are plain strings rather than a type parameter: a generic component
 * cannot be wrapped in `React.memo` without asserting its type back, and the
 * caller already owns the constant it passes in.
 */
export const SegmentedControl = React.memo(function SegmentedControl({
  ariaLabel,
  className,
  onChange,
  options,
  value,
}: SegmentedControlProps): JSX.Element {
  const { classes, cx } = useStyles();
  return (
    <div aria-label={ariaLabel} className={cx(classes.root, className)} role="group">
      {options.map(option => (
        <SegmentedControlButton
          key={option.value}
          option={option}
          selected={option.value === value}
          onChange={onChange}
        />
      ))}
    </div>
  );
});
